import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, Trash2, Upload, Package, Euro, FileText, Boxes, X, Sparkles, BarChart3, Table as TableIcon, Paperclip, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from 'recharts';

const PLATFORMS = {
  ebay: { label: 'eBay', color: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
  vinted: { label: 'Vinted', color: 'bg-teal-500/10 text-teal-300 border-teal-500/30' },
  leboncoin: { label: 'LeBonCoin', color: 'bg-orange-500/10 text-orange-300 border-orange-500/30' },
  facebook: { label: 'Facebook Marketplace', color: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30' },
  autre: { label: 'Autre', color: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30' },
};

const STATUSES = {
  a_imprimer: { label: 'À imprimer', color: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30' },
  en_impression: { label: 'En impression', color: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
  termine: { label: 'Terminé', color: 'bg-amber-500/10 text-amber-300 border-amber-500/30' },
  expedie: { label: 'Expédié', color: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
};

const CARRIERS = [
  'Mondial Relay', 'La Poste', 'Colissimo', 'Chronopost',
  'DPD', 'GLS', 'UPS', 'DHL', 'FedEx', 'Relais Colis', 'Autre',
];

const EMPTY_FORM = {
  order_date: new Date().toISOString().split('T')[0],
  platform: 'ebay',
  order_number: '',
  customer_name: '',
  article_name: '',
  quantity: 1,
  sale_price: 0,
  production_status: 'a_imprimer',
  tracking_number: '',
  carrier: '',
  invoice_done: false,
  invoice_number: '',
  notes: '',
};

// Orange alert if status="termine" AND ready_at > 3 days ago
function isStaleReady(order) {
  if (order.production_status !== 'termine' || !order.ready_at) return false;
  const readyDate = new Date(order.ready_at);
  const diffDays = (Date.now() - readyDate.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays > 3;
}

export default function Print3DOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_revenue: 0, total_count: 0, in_production: 0, to_ship: 0, to_invoice: 0 });
  const [filters, setFilters] = useState({ platform: '', status: '', invoice: '', search: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null); // null=create, order-object=edit
  const [pendingLabelFile, setPendingLabelFile] = useState(null); // file to attach after create
  const [form, setForm] = useState(EMPTY_FORM);
  const [extracting, setExtracting] = useState(false);
  const [inlineExtractingId, setInlineExtractingId] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'analytics'
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const fileInputRef = useRef(null);
  const inlineFileInputRef = useRef(null);

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.status) params.append('status', filters.status);
      if (filters.invoice) params.append('invoice', filters.invoice);
      if (filters.search) params.append('search', filters.search);
      const [{ data: orders }, { data: stats }] = await Promise.all([
        api.get(`/print3d-orders?${params}`),
        api.get('/print3d-orders/stats'),
      ]);
      setOrders(orders);
      setStats(stats);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const fetchAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const { data } = await api.get('/print3d-orders/analytics');
      setAnalytics(data);
    } catch {
      toast.error('Erreur chargement analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'analytics') fetchAnalytics();
  }, [viewMode, orders.length, fetchAnalytics]);

  const openCreateDialog = () => {
    setEditingOrder(null);
    setForm(EMPTY_FORM);
    setPendingLabelFile(null);
    setDialogOpen(true);
  };

  const openEditDialog = (order) => {
    setEditingOrder(order);
    setPendingLabelFile(null);
    setForm({
      order_date: order.order_date || new Date().toISOString().split('T')[0],
      platform: order.platform || 'ebay',
      order_number: order.order_number || '',
      customer_name: order.customer_name || '',
      article_name: order.article_name || '',
      quantity: order.quantity || 1,
      sale_price: order.sale_price || 0,
      production_status: order.production_status || 'a_imprimer',
      tracking_number: order.tracking_number || '',
      carrier: order.carrier || '',
      invoice_done: !!order.invoice_done,
      invoice_number: order.invoice_number || '',
      notes: order.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.article_name.trim()) {
      toast.error('Nom de l\'article requis');
      return;
    }
    try {
      let orderId = editingOrder?.id;
      if (editingOrder) {
        await api.patch(`/print3d-orders/${editingOrder.id}`, form);
        toast.success('Commande mise à jour');
      } else {
        const { data } = await api.post('/print3d-orders', form);
        orderId = data.id;
        toast.success('Commande ajoutée');
      }
      // If a label file was picked in the dialog and no persisted label yet, upload it now
      if (pendingLabelFile && orderId && !editingOrder?.label_file_id) {
        try {
          const fd = new FormData();
          fd.append('file', pendingLabelFile);
          await api.post(`/print3d-orders/${orderId}/attach-label`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('Étiquette enregistrée sur la commande');
        } catch (e) {
          toast.error('Étiquette non enregistrée: ' + (e?.response?.data?.detail || 'erreur'));
        }
      }
      setDialogOpen(false);
      setEditingOrder(null);
      setPendingLabelFile(null);
      setForm(EMPTY_FORM);
      fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const handleInlineUpdate = async (id, patch) => {
    try {
      await api.patch(`/print3d-orders/${id}`, patch);
      // Optimistic update
      setOrders(prev => prev.map(o => o.id === id ? { ...o, ...patch } : o));
      // Refresh stats in the background
      api.get('/print3d-orders/stats').then(({ data }) => setStats(data)).catch(() => {});
    } catch (err) {
      toast.error('Erreur de mise à jour');
      fetchOrders();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette commande ?')) return;
    try {
      await api.delete(`/print3d-orders/${id}`);
      toast.success('Commande supprimée');
      fetchOrders();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // In-dialog label picker: run extraction only. If we're editing an existing order,
  // ALSO persist immediately via attach-label. Otherwise, hold the file until save.
  const handleExtractLabel = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      if (editingOrder?.id) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post(`/print3d-orders/${editingOrder.id}/attach-label`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const ex = data.extracted || {};
        setForm(f => ({
          ...f,
          tracking_number: ex.tracking_number || f.tracking_number,
          carrier: ex.carrier || f.carrier,
          customer_name: !f.customer_name && ex.recipient_name ? ex.recipient_name : f.customer_name,
        }));
        // Also reflect on the editingOrder so the badge shows in the dialog
        setEditingOrder(o => o ? { ...o, label_file_id: data.file_id, label_filename: data.filename } : o);
        toast.success('Étiquette enregistrée + extraction OK');
      } else {
        // No order yet — extract-only + remember the file to upload after save
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/print3d-orders/extract-label', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setForm(f => ({
          ...f,
          tracking_number: data.tracking_number || f.tracking_number,
          carrier: data.carrier || f.carrier,
          customer_name: !f.customer_name && data.recipient_name ? data.recipient_name : f.customer_name,
        }));
        setPendingLabelFile(file);
        toast.success(`Extrait: ${data.tracking_number || 'aucun N°'} · ${data.carrier || '?'} · Fichier sera enregistré à la validation`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Extraction impossible');
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Extract label — inline on a specific order row (persisted)
  const handleInlineExtract = async (orderId, file) => {
    if (!file) return;
    setInlineExtractingId(orderId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/print3d-orders/${orderId}/attach-label`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const ex = data.extracted || {};
      // Refresh the row from server-state so we get label_file_id + label_filename
      const { data: refreshed } = await api.get('/print3d-orders?' + new URLSearchParams(
        Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
      ));
      setOrders(refreshed);
      if (ex.tracking_number || ex.carrier) {
        toast.success(`Étiquette enregistrée · ${ex.tracking_number || ''} ${ex.carrier ? '· ' + ex.carrier : ''}`);
      } else {
        toast.success('Étiquette enregistrée (aucune info détectée par l\'IA)');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Extraction impossible');
    } finally {
      setInlineExtractingId(null);
      if (inlineFileInputRef.current) inlineFileInputRef.current.value = '';
    }
  };

  const handleDeleteLabel = async (orderId) => {
    if (!window.confirm('Supprimer l\'étiquette de cette commande ?')) return;
    try {
      await api.delete(`/print3d-orders/${orderId}/label`);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, label_file_id: null, label_filename: null } : o));
      toast.success('Étiquette supprimée');
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-6" data-testid="print3d-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-50 flex items-center gap-2">
            <Boxes className="w-7 h-7 text-amber-500" /> Impression 3D
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Ventes en ligne — eBay, Vinted, LeBonCoin, Facebook Marketplace</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-zinc-900 border border-zinc-800 rounded-sm p-0.5" data-testid="p3d-view-toggle">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-xs font-medium rounded-sm flex items-center gap-1.5 transition-colors ${viewMode === 'table' ? 'bg-amber-500 text-zinc-950' : 'text-zinc-400 hover:text-zinc-100'}`}
              data-testid="p3d-view-table"
            >
              <TableIcon className="w-3.5 h-3.5" /> Tableau
            </button>
            <button
              onClick={() => setViewMode('analytics')}
              className={`px-3 py-1.5 text-xs font-medium rounded-sm flex items-center gap-1.5 transition-colors ${viewMode === 'analytics' ? 'bg-amber-500 text-zinc-950' : 'text-zinc-400 hover:text-zinc-100'}`}
              data-testid="p3d-view-analytics"
            >
              <BarChart3 className="w-3.5 h-3.5" /> Analytics
            </button>
          </div>
          <Button onClick={openCreateDialog} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-semibold" data-testid="p3d-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle commande
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Euro} label="CA total" value={formatCurrency(stats.total_revenue)} accent="text-emerald-400" testId="stat-revenue" />
        <StatCard icon={Package} label="En production" value={stats.in_production} accent="text-blue-400" testId="stat-in-production" />
        <StatCard icon={Package} label="À expédier" value={stats.to_ship} accent="text-amber-400" testId="stat-to-ship" />
        <StatCard icon={FileText} label="À facturer" value={stats.to_invoice} accent="text-rose-400" testId="stat-to-invoice" />
      </div>

      {/* Filters + Table */}
      {viewMode === 'table' && (
      <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Rechercher (n° cmd, client, article, suivi)"
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="p3d-search"
          />
        </div>
        <Select value={filters.platform || 'all'} onValueChange={v => setFilters(f => ({ ...f, platform: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="p3d-filter-platform"><SelectValue placeholder="Plateforme" /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <SelectItem value="all">Toutes plateformes</SelectItem>
            {Object.entries(PLATFORMS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="p3d-filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.invoice || 'all'} onValueChange={v => setFilters(f => ({ ...f, invoice: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="p3d-filter-invoice"><SelectValue placeholder="Facturation" /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <SelectItem value="all">Toutes factures</SelectItem>
            <SelectItem value="yes">Facturées</SelectItem>
            <SelectItem value="no">Non facturées</SelectItem>
          </SelectContent>
        </Select>
        {(filters.platform || filters.status || filters.invoice || filters.search) && (
          <Button variant="outline" size="sm" onClick={() => setFilters({ platform: '', status: '', invoice: '', search: '' })} className="border-zinc-700 text-zinc-300">
            <X className="w-3 h-3 mr-1" /> Effacer
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Date</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Plateforme</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">N° cmd</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Client</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Article</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider text-center">Qté</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider text-right">Prix</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Statut</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Suivi</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Facture</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} className="text-center text-zinc-500 py-10">Chargement…</TableCell></TableRow>
            ) : orders.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center text-zinc-500 py-10">Aucune commande. Ajoutez la première !</TableCell></TableRow>
            ) : orders.map(o => (
              <OrderRow
                key={o.id}
                order={o}
                onUpdate={patch => handleInlineUpdate(o.id, patch)}
                onDelete={() => handleDelete(o.id)}
                onEdit={() => openEditDialog(o)}
                onExtractLabel={file => handleInlineExtract(o.id, file)}
                onDeleteLabel={() => handleDeleteLabel(o.id)}
                isExtracting={inlineExtractingId === o.id}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      </>
      )}

      {/* Analytics view */}
      {viewMode === 'analytics' && (
        <AnalyticsView analytics={analytics} loading={loadingAnalytics} />
      )}

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingOrder ? 'Modifier la commande — Impression 3D' : 'Nouvelle commande — Impression 3D'}</DialogTitle>
            <DialogDescription className="text-zinc-500">Vente en ligne (eBay / Vinted / LBC / autre)</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Date de commande">
              <Input type="date" value={form.order_date} onChange={e => setForm({ ...form, order_date: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="p3d-form-date" />
            </Field>
            <Field label="Plateforme">
              <Select value={form.platform} onValueChange={v => setForm({ ...form, platform: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="p3d-form-platform"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {Object.entries(PLATFORMS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="N° commande plateforme">
              <Input value={form.order_number} onChange={e => setForm({ ...form, order_number: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="p3d-form-order-number" />
            </Field>
            <Field label="Nom client / pseudo">
              <Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="p3d-form-customer" />
            </Field>
            <Field label="Article *">
              <Input value={form.article_name} onChange={e => setForm({ ...form, article_name: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="p3d-form-article" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Quantité">
                <Input type="number" min={1} value={form.quantity} onChange={e => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })} className="bg-zinc-900 border-zinc-800" data-testid="p3d-form-qty" />
              </Field>
              <Field label="Prix (€)">
                <Input type="number" step="0.01" value={form.sale_price} onChange={e => setForm({ ...form, sale_price: parseFloat(e.target.value) || 0 })} className="bg-zinc-900 border-zinc-800" data-testid="p3d-form-price" />
              </Field>
            </div>
            <Field label="Statut production">
              <Select value={form.production_status} onValueChange={v => setForm({ ...form, production_status: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            <div className="md:col-span-2 border-t border-zinc-800 pt-3 mt-1">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Bon d'envoi</p>
                <label className="cursor-pointer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={e => handleExtractLabel(e.target.files?.[0])}
                    data-testid="p3d-form-label-upload"
                  />
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border ${extracting ? 'bg-amber-500/10 text-amber-300 border-amber-500/30 animate-pulse' : 'bg-zinc-900 text-amber-400 border-amber-500/30 hover:bg-amber-500/10'}`}>
                    <Sparkles className="w-3 h-3" />
                    {extracting ? 'Extraction…' : 'Extraire depuis l\'étiquette (IA)'}
                  </span>
                </label>
              </div>
              {/* Current attached label (edit mode) */}
              {editingOrder?.label_file_id && (
                <div className="mb-2 flex items-center justify-between gap-2 bg-emerald-500/5 border border-emerald-500/30 rounded-sm px-3 py-2">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${editingOrder.label_file_id}?auth=${encodeURIComponent(localStorage.getItem('pp_access_token') || '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-emerald-300 hover:text-emerald-200"
                    data-testid="p3d-form-label-link"
                  >
                    <Paperclip className="w-4 h-4" />
                    <span className="truncate max-w-[300px]">{editingOrder.label_filename || 'Voir l\'étiquette'}</span>
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm('Supprimer l\'étiquette de cette commande ?')) return;
                      try {
                        await api.delete(`/print3d-orders/${editingOrder.id}/label`);
                        setEditingOrder(o => o ? { ...o, label_file_id: null, label_filename: null } : o);
                        toast.success('Étiquette supprimée');
                      } catch {
                        toast.error('Erreur');
                      }
                    }}
                    className="text-rose-400 hover:text-rose-300 text-xs"
                    data-testid="p3d-form-label-delete"
                  >
                    Supprimer
                  </button>
                </div>
              )}
              {pendingLabelFile && !editingOrder && (
                <div className="mb-2 flex items-center gap-2 bg-amber-500/5 border border-amber-500/30 rounded-sm px-3 py-2 text-xs text-amber-300">
                  <Paperclip className="w-3.5 h-3.5" />
                  <span className="truncate">{pendingLabelFile.name}</span>
                  <span className="text-zinc-500 ml-auto">sera enregistrée à la validation</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="N° de suivi"
                  value={form.tracking_number}
                  onChange={e => setForm({ ...form, tracking_number: e.target.value })}
                  className="bg-zinc-900 border-zinc-800"
                  data-testid="p3d-form-tracking"
                />
                <Select value={form.carrier || 'none'} onValueChange={v => setForm({ ...form, carrier: v === 'none' ? '' : v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="p3d-form-carrier"><SelectValue placeholder="Transporteur" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem value="none">—</SelectItem>
                    {CARRIERS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="md:col-span-2 border-t border-zinc-800 pt-3">
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-2">Facturation</p>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
                  <Checkbox checked={form.invoice_done} onCheckedChange={v => setForm({ ...form, invoice_done: !!v })} data-testid="p3d-form-invoice-done" />
                  Facture faite
                </label>
                <Input
                  placeholder="N° de facture"
                  value={form.invoice_number}
                  onChange={e => setForm({ ...form, invoice_number: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 flex-1"
                  data-testid="p3d-form-invoice-number"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <Field label="Notes">
                <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-zinc-900 border-zinc-800" placeholder="Optionnel" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700 text-zinc-300">Annuler</Button>
            <Button onClick={handleSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-semibold" data-testid="p3d-form-submit">{editingOrder ? 'Enregistrer' : 'Ajouter'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-zinc-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-3 flex items-center gap-3" data-testid={testId}>
      <Icon className={`w-8 h-8 ${accent}`} />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{label}</p>
        <p className={`text-lg font-bold ${accent} truncate`}>{value}</p>
      </div>
    </div>
  );
}

// Distinctive palette per platform — matches the badge colors used in the table
const PLATFORM_COLORS = {
  ebay: '#3b82f6',        // blue
  vinted: '#14b8a6',      // teal
  leboncoin: '#f97316',   // orange
  facebook: '#6366f1',    // indigo
  autre: '#a1a1aa',       // zinc
};

const STATUS_COLORS = {
  a_imprimer: '#a1a1aa',
  en_impression: '#3b82f6',
  termine: '#f59e0b',
  expedie: '#10b981',
};

function AnalyticsView({ analytics, loading }) {
  if (loading || !analytics) {
    return (
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm py-16 text-center text-zinc-500" data-testid="p3d-analytics-loading">
        Chargement des analytics…
      </div>
    );
  }
  const byPlatform = (analytics.by_platform || []).map(p => ({
    ...p,
    label: PLATFORMS[p.platform]?.label || p.platform,
    color: PLATFORM_COLORS[p.platform] || '#a1a1aa',
  }));
  const totalRevenue = byPlatform.reduce((a, b) => a + (b.revenue || 0), 0);
  const totalCount = byPlatform.reduce((a, b) => a + (b.count || 0), 0);
  const byMonth = (analytics.by_month || []).map(m => ({
    ...m,
    monthLabel: formatMonthLabel(m.month),
  }));
  const byStatus = (analytics.by_status || []).map(s => ({
    ...s,
    label: STATUSES[s.status]?.label || s.status,
    color: STATUS_COLORS[s.status] || '#a1a1aa',
  }));
  const topArticles = analytics.top_articles || [];

  const isEmpty = totalCount === 0;

  if (isEmpty) {
    return (
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm py-16 text-center text-zinc-500" data-testid="p3d-analytics-empty">
        <BarChart3 className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
        <p>Aucune donnée à afficher pour l'instant.</p>
        <p className="text-xs text-zinc-600 mt-1">Ajoutez des commandes pour voir vos statistiques par plateforme.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="p3d-analytics">
      {/* Row 1: Donut (revenue by platform) + platform ranking */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-zinc-100">CA par plateforme</h3>
              <p className="text-xs text-zinc-500">{totalCount} commande{totalCount > 1 ? 's' : ''} · {formatCurrency(totalRevenue)}</p>
            </div>
            <BarChart3 className="w-4 h-4 text-amber-500" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={byPlatform}
                  dataKey="revenue"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={2}
                  stroke="none"
                >
                  {byPlatform.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 2, fontSize: 12 }}
                  formatter={(value, name) => [formatCurrency(value), name]}
                />
                <Legend
                  verticalAlign="bottom"
                  iconType="circle"
                  wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Ranking / table */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4">
          <h3 className="text-sm font-semibold text-zinc-100 mb-3">Classement plateformes</h3>
          <div className="space-y-2">
            {byPlatform.map(p => {
              const pct = totalRevenue > 0 ? (p.revenue / totalRevenue) * 100 : 0;
              return (
                <div key={p.platform} data-testid={`p3d-rank-${p.platform}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <div className="flex items-center gap-1.5 text-zinc-200">
                      <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                      {p.label}
                    </div>
                    <div className="text-zinc-400 font-medium">{formatCurrency(p.revenue)} <span className="text-zinc-600 text-[10px]">· {p.count}</span></div>
                  </div>
                  <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${pct}%`, background: p.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Row 2: Revenue over time */}
      {byMonth.length > 0 && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-zinc-100">Évolution du chiffre d'affaires</h3>
              <p className="text-xs text-zinc-500">Sur les {byMonth.length} derniers mois</p>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={byMonth} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="monthLabel" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} tickFormatter={v => `${v}€`} />
                <Tooltip
                  contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 2, fontSize: 12 }}
                  formatter={(value, name) => name === 'revenue' ? [formatCurrency(value), 'CA'] : [value, 'Commandes']}
                  labelStyle={{ color: '#e4e4e7' }}
                />
                <Line type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Row 3: Status distribution + top articles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4">
          <h3 className="text-sm font-semibold text-zinc-100 mb-3">Répartition par statut</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byStatus} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 2, fontSize: 12 }}
                  cursor={{ fill: 'rgba(245,158,11,0.05)' }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {byStatus.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4" data-testid="p3d-top-articles">
          <h3 className="text-sm font-semibold text-zinc-100 mb-3">Top 5 articles (CA)</h3>
          {topArticles.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-8">—</p>
          ) : (
            <div className="space-y-3">
              {topArticles.map((a, i) => {
                const max = topArticles[0].revenue || 1;
                const pct = (a.revenue / max) * 100;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-zinc-200 truncate mr-2">{a.name}</span>
                      <span className="text-amber-400 font-medium whitespace-nowrap">{formatCurrency(a.revenue)} <span className="text-zinc-600 text-[10px]">· {a.count}</span></span>
                    </div>
                    <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatMonthLabel(ym) {
  // ym = "YYYY-MM"
  if (!ym || ym.length < 7) return ym;
  const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
  const [y, m] = ym.split('-');
  const idx = parseInt(m, 10) - 1;
  return `${months[idx] || m} ${y.slice(2)}`;
}



function OrderRow({ order, onUpdate, onDelete, onEdit, onExtractLabel, onDeleteLabel, isExtracting }) {
  const [localTracking, setLocalTracking] = useState(order.tracking_number || '');
  const [localInvoiceNumber, setLocalInvoiceNumber] = useState(order.invoice_number || '');
  const [localArticle, setLocalArticle] = useState(order.article_name || '');
  const [localCustomer, setLocalCustomer] = useState(order.customer_name || '');
  const [localOrderNumber, setLocalOrderNumber] = useState(order.order_number || '');
  const [localPrice, setLocalPrice] = useState(order.sale_price || 0);
  const [localQty, setLocalQty] = useState(order.quantity || 1);
  const fileRef = useRef(null);

  useEffect(() => { setLocalTracking(order.tracking_number || ''); }, [order.tracking_number]);
  useEffect(() => { setLocalInvoiceNumber(order.invoice_number || ''); }, [order.invoice_number]);
  useEffect(() => { setLocalArticle(order.article_name || ''); }, [order.article_name]);
  useEffect(() => { setLocalCustomer(order.customer_name || ''); }, [order.customer_name]);
  useEffect(() => { setLocalOrderNumber(order.order_number || ''); }, [order.order_number]);
  useEffect(() => { setLocalPrice(order.sale_price || 0); }, [order.sale_price]);
  useEffect(() => { setLocalQty(order.quantity || 1); }, [order.quantity]);

  const stale = isStaleReady(order);
  const platform = PLATFORMS[order.platform] || PLATFORMS.autre;
  const status = STATUSES[order.production_status] || STATUSES.a_imprimer;

  const commitIfChanged = (key, localVal, originalVal) => {
    if (localVal !== originalVal) onUpdate({ [key]: localVal });
  };

  return (
    <TableRow className={`border-zinc-800 ${stale ? 'bg-amber-500/5' : ''}`} data-testid={`p3d-row-${order.id}`}>
      <TableCell className="text-zinc-300 text-sm whitespace-nowrap">{formatDate(order.order_date)}</TableCell>
      <TableCell>
        <Select value={order.platform} onValueChange={v => onUpdate({ platform: v })}>
          <SelectTrigger className={`h-7 text-xs w-32 bg-transparent border ${platform.color}`} data-testid={`p3d-platform-${order.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            {Object.entries(PLATFORMS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Input
          value={localOrderNumber}
          onChange={e => setLocalOrderNumber(e.target.value)}
          onBlur={() => commitIfChanged('order_number', localOrderNumber, order.order_number || '')}
          className="h-7 text-xs bg-transparent border-zinc-800 hover:border-zinc-700 focus:border-amber-500 w-28"
          placeholder="—"
          data-testid={`p3d-order-num-${order.id}`}
        />
      </TableCell>
      <TableCell>
        <Input
          value={localCustomer}
          onChange={e => setLocalCustomer(e.target.value)}
          onBlur={() => commitIfChanged('customer_name', localCustomer, order.customer_name || '')}
          className="h-7 text-xs bg-transparent border-zinc-800 hover:border-zinc-700 focus:border-amber-500 w-32"
          placeholder="—"
          data-testid={`p3d-customer-${order.id}`}
        />
      </TableCell>
      <TableCell>
        <Input
          value={localArticle}
          onChange={e => setLocalArticle(e.target.value)}
          onBlur={() => commitIfChanged('article_name', localArticle, order.article_name || '')}
          className="h-7 text-xs bg-transparent border-zinc-800 hover:border-zinc-700 focus:border-amber-500 min-w-[160px]"
          data-testid={`p3d-article-${order.id}`}
        />
      </TableCell>
      <TableCell className="text-center">
        <Input
          type="number"
          min={1}
          value={localQty}
          onChange={e => setLocalQty(parseInt(e.target.value) || 1)}
          onBlur={() => commitIfChanged('quantity', localQty, order.quantity || 1)}
          className="h-7 text-xs bg-transparent border-zinc-800 hover:border-zinc-700 focus:border-amber-500 w-14 text-center"
          data-testid={`p3d-qty-${order.id}`}
        />
      </TableCell>
      <TableCell className="text-right">
        <Input
          type="number"
          step="0.01"
          value={localPrice}
          onChange={e => setLocalPrice(parseFloat(e.target.value) || 0)}
          onBlur={() => commitIfChanged('sale_price', localPrice, order.sale_price || 0)}
          className="h-7 text-xs bg-transparent border-zinc-800 hover:border-zinc-700 focus:border-amber-500 w-20 text-right"
          data-testid={`p3d-price-${order.id}`}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Select value={order.production_status} onValueChange={v => onUpdate({ production_status: v })}>
            <SelectTrigger className={`h-7 text-xs w-32 bg-transparent border ${status.color}`} data-testid={`p3d-status-${order.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              {Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {stale && (
            <span title="Prêt depuis plus de 3 jours" className="text-amber-400 text-[10px] font-bold" data-testid={`p3d-stale-${order.id}`}>
              &gt;3j
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Input
            value={localTracking}
            onChange={e => setLocalTracking(e.target.value)}
            onBlur={() => commitIfChanged('tracking_number', localTracking, order.tracking_number || '')}
            placeholder="—"
            className="h-7 text-xs bg-transparent border-zinc-800 hover:border-zinc-700 focus:border-amber-500 w-28"
            data-testid={`p3d-tracking-${order.id}`}
          />
          <Select value={order.carrier || 'none'} onValueChange={v => onUpdate({ carrier: v === 'none' ? '' : v })}>
            <SelectTrigger className="h-7 text-[10px] w-24 bg-transparent border-zinc-800" data-testid={`p3d-carrier-${order.id}`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              <SelectItem value="none">—</SelectItem>
              {CARRIERS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="cursor-pointer" title="Uploader l'étiquette (PDF/image) — extraction IA automatique + fichier conservé">
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={e => onExtractLabel(e.target.files?.[0])}
              data-testid={`p3d-extract-${order.id}`}
            />
            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-sm border transition-colors ${isExtracting ? 'bg-amber-500/20 border-amber-500/50 animate-pulse' : 'border-zinc-800 hover:border-amber-500/50 hover:bg-amber-500/10'}`}>
              {isExtracting ? <Sparkles className="w-3 h-3 text-amber-400 animate-spin" /> : <Upload className="w-3 h-3 text-zinc-400" />}
            </span>
          </label>
          {order.label_file_id && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${order.label_file_id}?auth=${encodeURIComponent(localStorage.getItem('pp_access_token') || '')}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center w-7 h-7 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
              title={`Voir l'étiquette : ${order.label_filename || 'étiquette'}`}
              data-testid={`p3d-label-view-${order.id}`}
              onContextMenu={e => {
                e.preventDefault();
                if (onDeleteLabel) onDeleteLabel();
              }}
            >
              <Paperclip className="w-3 h-3" />
            </a>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!order.invoice_done}
            onCheckedChange={v => onUpdate({ invoice_done: !!v })}
            data-testid={`p3d-invoice-done-${order.id}`}
          />
          <Input
            value={localInvoiceNumber}
            onChange={e => setLocalInvoiceNumber(e.target.value)}
            onBlur={() => commitIfChanged('invoice_number', localInvoiceNumber, order.invoice_number || '')}
            placeholder="N° facture"
            className="h-7 text-xs bg-transparent border-zinc-800 hover:border-zinc-700 focus:border-amber-500 w-24"
            data-testid={`p3d-invoice-num-${order.id}`}
          />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="text-zinc-500 hover:text-amber-400 p-1" data-testid={`p3d-edit-${order.id}`} title="Modifier la commande">
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={onDelete} className="text-zinc-500 hover:text-rose-400 p-1" data-testid={`p3d-delete-${order.id}`} title="Supprimer">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}
